"use client";

import { useEffect } from "react";

// Ports the scroll behaviours from the approved mockup: data-parallax translate,
// and reveal-on-intersect (incl. staggered groups). Reduced-motion safe.
export function useSiteEffects() {
  useEffect(() => {
    const prefersReduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // ---------- Parallax ----------
    let removeParallax = () => {};
    if (!prefersReduce) {
      const pxItems = Array.from(
        document.querySelectorAll<HTMLElement>("[data-parallax]"),
      ).map((el) => ({
        el,
        speed: parseFloat(el.getAttribute("data-parallax") || "") || 0.2,
      }));
      let ticking = false;
      const updatePx = () => {
        const vh = window.innerHeight;
        for (const { el, speed } of pxItems) {
          const rect = el.getBoundingClientRect();
          const center = rect.top + rect.height / 2;
          const delta = center - vh / 2;
          const ty = -delta * speed;
          el.style.transform = `translate3d(0, ${ty.toFixed(2)}px, 0)`;
        }
        ticking = false;
      };
      const onPxScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(updatePx);
        }
      };
      window.addEventListener("scroll", onPxScroll, { passive: true });
      window.addEventListener("resize", onPxScroll);
      updatePx();
      removeParallax = () => {
        window.removeEventListener("scroll", onPxScroll);
        window.removeEventListener("resize", onPxScroll);
      };
    }

    // ---------- Reveal on intersect ----------
    const revealSelectors = [
      ".eyebrow",
      "section h2.display",
      ".context p",
      ".pivot .display",
      ".gaze .principles",
      ".gaze p.note",
      ".iphone-stage",
      ".callout",
      ".experience .sub",
      ".perspective",
      ".identity h2",
      ".identity .pillar",
      ".identity .closer",
      ".when h2",
      ".when-row",
      ".when .lighthouse",
      ".ethics h2",
      ".ethics-point",
      ".pricing h2",
      ".pricing .head p",
      ".plan",
      ".b2b-inner",
      ".contact h2",
      ".contact p.intro",
      ".contact .info",
      ".contact-form",
    ];
    const staggerSelector = ".context-chips, .sub-scores, .when-list, .plans, .pillars";
    const revealed: Element[] = [];

    if (!prefersReduce && "IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              e.target.classList.add("in");
              io.unobserve(e.target);
            }
          }
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.05 },
      );

      document.querySelectorAll(revealSelectors.join(",")).forEach((el) => {
        el.classList.add("reveal");
        revealed.push(el);
        io.observe(el);
      });
      document.querySelectorAll(staggerSelector).forEach((grp) => {
        grp.classList.add("reveal-stagger");
        revealed.push(grp);
        io.observe(grp);
      });

      return () => {
        io.disconnect();
        removeParallax();
        revealed.forEach((el) => el.classList.remove("reveal", "reveal-stagger", "in"));
      };
    }

    return () => removeParallax();
  }, []);
}
